// 0615k ADR-024 + filosofia CEO: modal viewer-only de Persona.
//
// Sustituye la navegacion previa a /structure?tab=administracion&personaId=X
// (que abria el modal AdminPersonaPanel con todas las tabs CRUD) cuando se
// pincha en una persona desde:
//   - OrganigramaPage (click sobre nodo persona)
//   - PeopleAnalyticsDashboard / NegocioDashboard / GobernanzaDashboard
//
// Solo muestra datos basicos: identificacion, puesto actual, departamento,
// organizacion, centro principal, supervisor, roles gobernanza activos.
// SIN acciones de mutacion (esas viven en tab Administracion del modulo).

import { useEffect, useState } from 'react';
import { fetchPersona, type PersonaDetalle } from '../../../shared/services/api';

interface PersonaViewerModalProps {
  personaId: string | null;
  onClose: () => void;
}

const COLORS = {
  OSCURO: '#0E243E',
  AZUL1: '#1F3864',
  AZUL2: '#2F5496',
  CELESTE: '#33BCE1',
  GRIS_OSCURO: '#374151',
  GRIS_MEDIO: '#9CA3AF',
  GRIS_SUAVE: '#F3F4F6',
  BORDE: '#E5E7EB',
};

function getInitials(nombre: string, apellidos: string | null): string {
  const a = (nombre || '').trim()[0] ?? '';
  const b = (apellidos || '').trim()[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: COLORS.GRIS_MEDIO,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: COLORS.OSCURO, fontWeight: 500 }}>
        {value && value.trim().length > 0 ? (
          value
        ) : (
          <span style={{ color: COLORS.GRIS_MEDIO }}>—</span>
        )}
      </div>
    </div>
  );
}

export function PersonaViewerModal({ personaId, onClose }: PersonaViewerModalProps) {
  const [data, setData] = useState<PersonaDetalle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!personaId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPersona(personaId)
      .then((res) => {
        if (cancelled) return;
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error cargando persona');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [personaId]);

  // Cerrar con tecla ESC
  useEffect(() => {
    if (!personaId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [personaId, onClose]);

  if (!personaId) return null;

  const persona = data?.persona;
  const puestoActual = data?.puestoActual;
  const supervisor = data?.supervisorDerivado;
  const centroPrincipal = data?.centros?.find((c) => c.esPrincipal) ?? data?.centros?.[0];
  const rolesActivos = data?.rolesGobernanza ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Detalle de persona (solo lectura)"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(14,36,62,0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'inherit',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          padding: 24,
        }}
      >
        {/* Cerrar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: COLORS.GRIS_MEDIO,
              padding: 4,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.GRIS_MEDIO, fontSize: 13 }}>
            Cargando datos de la persona...
          </div>
        )}

        {error && !loading && (
          <div
            style={{
              padding: 16,
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              color: '#DC2626',
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {persona && !loading && !error && (
          <>
            {/* Cabecera con avatar + nombre + codigo */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: COLORS.AZUL2,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  flexShrink: 0,
                }}
              >
                {getInitials(persona.nombre, persona.apellidos)}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 700,
                    color: COLORS.OSCURO,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {persona.nombre} {persona.apellidos ?? ''}
                </h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                  {persona.codigo && (
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: COLORS.GRIS_OSCURO,
                        fontWeight: 700,
                      }}
                    >
                      {persona.codigo}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: COLORS.GRIS_SUAVE,
                      color: COLORS.GRIS_OSCURO,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    {persona.tipoPersona}
                  </span>
                  {persona.estado && persona.estado !== 'ACTIVO' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: '#FEF2F2',
                        color: '#DC2626',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                      }}
                    >
                      {persona.estado}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Datos identificativos */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 14,
                marginBottom: 20,
                padding: 14,
                background: COLORS.GRIS_SUAVE,
                borderRadius: 8,
              }}
            >
              <Field label="Email" value={persona.email} />
              <Field label="Teléfono" value={persona.telefono} />
              <Field label="Tipo" value={persona.tipoPersona} />
              <Field
                label="Centro principal"
                value={
                  centroPrincipal
                    ? `${centroPrincipal.centro.nombre}${centroPrincipal.centro.ciudad ? ' · ' + centroPrincipal.centro.ciudad : ''}`
                    : null
                }
              />
            </div>

            {/* Puesto actual */}
            <h3
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: COLORS.AZUL1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 8,
              }}
            >
              Puesto actual
            </h3>
            {puestoActual ? (
              <div
                style={{
                  border: `1px solid ${COLORS.BORDE}`,
                  borderLeft: `3px solid ${COLORS.AZUL2}`,
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 16,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                <Field label="Título" value={puestoActual.puesto.titulo} />
                <Field label="Nivel" value={puestoActual.puesto.nivel?.replace(/_/g, ' ')} />
                <Field label="Departamento" value={puestoActual.departamento?.nombre} />
                <Field label="Organización" value={puestoActual.organizacion?.nombre} />
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.GRIS_MEDIO,
                  fontStyle: 'italic',
                  marginBottom: 16,
                }}
              >
                Sin puesto activo asignado.
              </div>
            )}

            {/* Supervisor derivado */}
            {supervisor && (
              <>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: COLORS.AZUL1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 8,
                  }}
                >
                  Supervisor directo
                </h3>
                <div
                  style={{
                    fontSize: 13,
                    color: COLORS.OSCURO,
                    marginBottom: 16,
                    padding: 10,
                    background: '#F0F4F8',
                    borderRadius: 6,
                  }}
                >
                  {supervisor.nombre} {supervisor.apellidos}
                  {supervisor.tituloPuesto && (
                    <span style={{ color: COLORS.GRIS_MEDIO, marginLeft: 6 }}>
                      · {supervisor.tituloPuesto}
                    </span>
                  )}
                </div>
              </>
            )}

            {/* Roles gobernanza activos */}
            {rolesActivos.length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: COLORS.AZUL1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 8,
                  }}
                >
                  Roles de gobernanza ({rolesActivos.length})
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                  {rolesActivos.map((r) => (
                    <span
                      key={r.id}
                      title={r.ambito ?? undefined}
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: 12,
                        background: '#EEF4FB',
                        color: COLORS.AZUL2,
                        border: `1px solid ${COLORS.CELESTE}`,
                      }}
                    >
                      {r.nombrePersonalizado || r.tipoRol.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </>
            )}

            {/* Footer informativo */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${COLORS.BORDE}`,
                fontSize: 11,
                color: COLORS.GRIS_MEDIO,
                fontStyle: 'italic',
              }}
            >
              Vista de solo lectura. Para editar la ficha de la persona ve a Estructura →
              Administración.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
